import { Close } from '@mui/icons-material';
import ArrowDownIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpIcon from '@mui/icons-material/ArrowUpward';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, List, ListItem, ListItemSecondaryAction, ListItemText } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { SpeakerEntry, SpeakersComponentInterface, StageComponentInterface } from '../../../../../../interfaces/pagebuilder';
import { fetchAndSetEventSpeakers } from '../../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../../reduxToolkit/typedRedux';
import { updateSpeaker } from '../../../../../../services/services';
import VyTooltip from '../../../../../common/VyTooltip';
import SpeakerForm from './SpeakerForm';

interface NewSpeakersProps {
  component: SpeakersComponentInterface | StageComponentInterface;
  onChange: (entryList: SpeakerEntry[]) => void;
  withHasModalOption?: boolean;
}

const SpeakersList = ({ component, onChange, withHasModalOption = true }: NewSpeakersProps) => {
  const [editingEntry, setEditingEntry] = useState<string | null>(null);
  const event = useSelector((state) => state.common.event);
  const eventSpeakers = useSelector((state) => state.common.eventSpeakers);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  function deleteEntry(index: number) {
    const newComp = { ...component };
    if (!newComp.entryIds) return;
    newComp.entryIds = newComp.entryIds.filter((_, i) => i !== index);
    onChange(newComp.entryIds);
  }

  function changePosition(index: number, newIndex: number) {
    const newComp = { ...component };
    if (!newComp.entryIds) return;
    newComp.entryIds = [...newComp.entryIds];
    const entryHolder = { ...newComp.entryIds[newIndex] };
    newComp.entryIds[newIndex] = { ...newComp.entryIds[index] };
    newComp.entryIds[index] = entryHolder;
    onChange(newComp.entryIds);
  }

  async function editEntry(props: SpeakerEntry) {
    const newComp = { ...component };
    if (editingEntry === null || !props?._id || !event || !newComp.entryIds) return;

    const speaker = await updateSpeaker(props._id, props);

    newComp.entryIds =
      newComp.entryIds?.map((e) => {
        if (e._id === editingEntry) return { ...e, ...speaker };
        return e;
      }) ?? [];

    setEditingEntry(null);
    onChange(newComp.entryIds);
    dispatch(fetchAndSetEventSpeakers(event._id));
  }

  return (
    <>
      <List className="entry-list" dense>
        {component.entryIds
          ?.filter((speaker: any) => !speaker.datetime_deleted)
          ?.filter((speaker) => eventSpeakers?.map((s) => s._id).includes(speaker._id))
          ?.map((entry, index, allSpeakers) => (
            <ListItem key={entry._id}>
              <ListItemText primary={entry.name} />
              <ListItemSecondaryAction>
                {
                  //@ts-ignore
                  component.sortBy !== 'ALPHABETIC' && index !== 0 && (
                    <IconButton onClick={() => changePosition(index, index - 1)} edge="end" aria-label="move up" size="large">
                      <ArrowUpIcon />
                    </IconButton>
                  )
                }

                {
                  //@ts-ignore
                  component.sortBy !== 'ALPHABETIC' && component.entryIds?.length && index !== component.entryIds.length - 1 && allSpeakers.length > 1 && (
                    <IconButton onClick={() => changePosition(index, index + 1)} edge="end" aria-label="move down" size="large">
                      <ArrowDownIcon />
                    </IconButton>
                  )
                }

                <IconButton onClick={() => entry._id && setEditingEntry(entry._id)} edge="end" aria-label="edit" size="large">
                  <EditIcon />
                </IconButton>
                <VyTooltip title={t('pagebuilder:remove-speaker')}>
                  <IconButton onClick={() => deleteEntry(index)} edge="end" aria-label="delete" size="large">
                    <Close />
                  </IconButton>
                </VyTooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
      </List>
      {editingEntry !== null && component.entryIds && (
        <SpeakerForm
          isModal
          isModalOpen={!!editEntry}
          defaultValues={component?.entryIds?.find((e) => e._id === editingEntry)}
          onSpeakerCreated={editEntry}
          onCloseModal={() => setEditingEntry(null)}
          withHasModalOption={withHasModalOption}
          applyDefaultStyleOnEditor={false}
        />
      )}
    </>
  );
};

export default SpeakersList;
